/**
 * V250: Simple Replace Photo Dialog - No Radix, No Portal, No Animations
 * This is a plain conditional overlay that unmounts immediately when closed
 * V277.4: Added PhotoPreviewEmpty for empty photo states
 */
import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PhotoPreviewEmpty from './PhotoPreviewEmpty';
import { resolveDocumentDirection } from '@/i18n/languages';

interface SimpleReplacePhotoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  newPhotoUrl: string;
  currentPhotoUrl: string;
  fallbackInitial?: string;
}

const SimpleReplacePhotoDialog: React.FC<SimpleReplacePhotoDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  newPhotoUrl,
  currentPhotoUrl,
  fallbackInitial = '?',
}) => {
  const { t, i18n } = useTranslation();
  const direction = resolveDocumentDirection(i18n.resolvedLanguage || i18n.language);

  if (!isOpen || !newPhotoUrl) return null;

  return (
    <div
      dir={direction}
      className="fixed inset-x-0 z-[70] bg-black/90"
      style={{
        // V275.7: Start BELOW status bar - don't cover it at all
        top: 'env(safe-area-inset-top, 0px)',
        bottom: 0,
        animation: 'none',
        transition: 'none',
        pointerEvents: 'auto',
      }}
      onClick={onClose}
    >
      {/* V275.7: Full-height dialog container - also starts below status bar */}
      <div
        className="absolute inset-x-0 bottom-0 flex flex-col"
        style={{
          // V275.7: Start below status bar (same as overlay)
          top: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          animation: 'none',
          transition: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex-1 min-h-0 w-full flex flex-col bg-slate-950/95 border-t border-white/15 shadow-2xl overflow-hidden"
          style={{
            animation: 'none',
            transition: 'none',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <h3 className="text-white text-lg font-semibold text-start">{t('camera.replacePhotoTitle')}</h3>
            <button
              onClick={onClose}
              className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              aria-label={t('camera.closeViewer')}
            >
              <X size={20} />
            </button>
          </div>

          {/* Body - flex-1 to take available space, centered content */}
          <div className="flex-1 px-4 py-3 overflow-hidden flex flex-col justify-center">
            <div className="flex flex-col items-center space-y-4">
              {/* New Photo - Large, centered */}
              <div className="flex flex-col items-center w-full">
                <div className="text-emerald-300 text-sm font-semibold mb-1 text-start">{t('camera.newPhoto')}</div>
                <div
                  className="replace-dialog-new-photo relative overflow-hidden rounded-2xl border-2 border-emerald-500/50 bg-black/20 aspect-[4/5]"
                  style={{
                    width: 'min(288px, 80vw, calc(38dvh * 4 / 5))',
                    maxHeight: '38dvh',
                  }}
                >
                  {/* V329: Allow data:, blob:, http - data URLs from native Base64 picker */}
                  {newPhotoUrl && (newPhotoUrl.startsWith('data:') || newPhotoUrl.startsWith('http') || newPhotoUrl.startsWith('blob:')) ? (
                    <img
                      key={newPhotoUrl || 'empty'}
                      src={newPhotoUrl || ''}
                      alt={t('camera.newPhotoPreviewAlt')}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ objectFit: 'cover', objectPosition: 'center' }}
                      draggable={false}
                      onLoad={() => {
                        if (newPhotoUrl?.startsWith('data:image/')) console.debug('[V329] dialog preview uses data URL');
                      }}
                      onError={() => {
                        console.warn('[V329] preview img error', newPhotoUrl?.slice(0, 48));
                      }}
                    />
                  ) : (
                    <PhotoPreviewEmpty message={t('camera.newPhotoPreviewAlt')} />
                  )}
                </div>
              </div>

              {/* Current Photo - Small */}
              <div className="flex flex-col items-center w-full">
                <div className="text-white/60 text-xs font-medium mb-1 text-start">{t('camera.currentPhoto')}</div>
                <div
                  className="replace-dialog-current-photo replace-photo-current-frame relative overflow-hidden rounded-xl bg-slate-800 border border-white/10 aspect-[4/5]"
                  style={{
                    width: '144px',
                  }}
                >
                  {/* V329: Allow data:, blob:, http */}
                  {currentPhotoUrl && (currentPhotoUrl.startsWith('data:') || currentPhotoUrl.startsWith('http') || currentPhotoUrl.startsWith('blob:')) ? (
                    <img
                      src={currentPhotoUrl}
                      alt={t('camera.currentPhotoAlt')}
                      className="absolute inset-0 w-full h-full"
                      style={{ objectFit: 'cover', objectPosition: 'center' }}
                      draggable={false}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-4xl text-slate-400">
                        {fallbackInitial?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer - centered buttons, stacked vertically */}
          <div className="px-5 py-4 border-t border-white/10">
            <div className="flex flex-col gap-3 w-full max-w-[320px] mx-auto">
              <button
                onClick={onConfirm}
                className="w-full h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-all active:scale-[0.98] p-0 text-center"
              >
                {t('camera.confirmReplace')}
              </button>
              <button
                onClick={onClose}
                className="replace-dialog-cancel-button w-full h-12 rounded-2xl bg-white/10 hover:bg-white/15 text-white font-semibold transition-all active:scale-[0.98] p-0 text-center"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleReplacePhotoDialog;

